package pl.nickers.match3

import pl.nickers.match3.ecs.FamiliarType
import pl.nickers.match3.ecs.PieceIds
import pl.nickers.match3.ecs.pieceIdForJellyType

data class TeleportLinkSpec(
    val srcRow: Int,
    val srcCol: Int,
    val destRow: Int,
    val destCol: Int,
)

data class TestLevel(
    val id: Int,
    val name: String,
    val grid: List<List<String>>,
    val moveLimit: Int? = null,
    val teleports: List<TeleportLinkSpec> = emptyList(),
    /** When true, [SpawnSystem] is disabled so the level cannot accumulate stray jellies. */
    val disableSpawn: Boolean = false,
    /** Collection objectives for this level. Empty → a random "collect 10" goal is generated. */
    val goals: List<LevelGoalConfig> = emptyList(),
    /** Familiar to bring into this level. Null → no familiar is created. */
    val familiar: FamiliarType? = null,
)

object TestLevels {

    /**
     * Builds a [height] × [width] (rows × columns) board of regular jellies, with
     * specific cells replaced by [overrides]. Defaults to a [GRID_SIZE] square so
     * existing levels (and their coordinate assertions) are unaffected; pass smaller
     * dimensions for levels that only need a handful of cells, which keeps the entity
     * count — and therefore the test — small.
     */
    fun baseBoard(
        overrides: Map<Pair<Int, Int>, String> = emptyMap(),
        width: Int = GRID_SIZE,
        height: Int = GRID_SIZE,
    ): List<List<String>> =
        List(height) { r ->
            List(width) { c ->
                overrides[r to c] ?: pieceIdForJellyType((r + 2 * c) % 6 + 1)
            }
        }

    /**
     * Builds a grid where every cell is a hole except those listed in [overrides].
     * Useful for minimal test levels that only need a handful of feature-specific
     * cells; the rest of the board is dead space the player cannot interact with.
     */
    fun holesBoard(
        overrides: Map<Pair<Int, Int>, String>,
        width: Int = GRID_SIZE,
        height: Int = GRID_SIZE,
    ): List<List<String>> =
        List(height) { r ->
            List(width) { c ->
                overrides[r to c] ?: GridTokens.HOLE
            }
        }

    val BOMB_DOUBLE_CLICK = TestLevel(
        id = 1,
        name = "Bomb: double-click to detonate",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.BOMB)),
    )

    val BOMB_SWAP_ACTIVATION = TestLevel(
        id = 2,
        name = "Bomb: swap with neighbour to detonate",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.BOMB)),
    )

    val LASER_H_SWAP_ACTIVATION = TestLevel(
        id = 3,
        name = "Horizontal laser: swap activation",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.LASER_HORIZONTAL)),
    )

    val LASER_H_DOUBLE_CLICK = TestLevel(
        id = 4,
        name = "Horizontal laser: double-click clears row",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.LASER_HORIZONTAL)),
    )

    val LASER_V_DOUBLE_CLICK = TestLevel(
        id = 5,
        name = "Vertical laser: double-click clears column",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.LASER_VERTICAL)),
    )

    val BOMB_TRIGGERS_LASER_IN_BLAST = TestLevel(
        id = 6,
        name = "Bomb chain: blast triggers adjacent laser",
        grid = baseBoard(
            mapOf(
                3 to 3 to PieceIds.BOMB,
                3 to 4 to PieceIds.LASER_HORIZONTAL,
            ),
        ),
    )

    val LASER_THROUGH_BOMB = TestLevel(
        id = 7,
        name = "Laser chain: beam detonates bomb in row",
        grid = baseBoard(
            mapOf(
                3 to 0 to PieceIds.LASER_HORIZONTAL,
                3 to 4 to PieceIds.BOMB,
            ),
        ),
    )

    val SWIRL_DOUBLE_CLICK = TestLevel(
        id = 8,
        name = "Colourful swirl: double-click activation",
        grid = baseBoard(mapOf(3 to 3 to PieceIds.COLOURFUL_SWIRL)),
    )

    val MOVE_LIMIT_LOST_GAME = TestLevel(
        id = 9,
        name = "Move limit: one move then lost",
        grid = baseBoard(),
        moveLimit = 1,
    )

    val MOVE_LIMIT_DECREMENT = TestLevel(
        id = 10,
        name = "Move limit: decrement without losing",
        grid = baseBoard(),
        moveLimit = 5,
    )

    val TARGETED_REMOVE = TestLevel(
        id = 11,
        name = "Targeted remove: tap to remove any entity",
        grid = baseBoard(
            mapOf(
                2 to 2 to PieceIds.CRYSTALLIZED_AMBER,
                2 to 4 to PieceIds.BOMB,
                4 to 2 to PieceIds.LASER_HORIZONTAL,
                4 to 4 to PieceIds.COLOURFUL_SWIRL,
            ),
        ),
    )

    val TELEPORT_BASIC = TestLevel(
        id = 12,
        name = "Teleport: bomb breaks amber, jelly drops through portal",
        // Minimal level: every cell is a hole except the four-cell channel at column 3.
        // The hole at (4,3) splits the channel so the broken-ice jelly cannot fall via
        // normal vertical/diagonal gravity — only the teleport pass from (3,3) to (5,3)
        // can fill the exit. Spawn is disabled so no stray jellies appear.
        grid = holesBoard(
            mapOf(
                2 to 3 to PieceIds.BOMB,
                3 to 3 to GridTokens.ICE_CUBE_LIFE_1,
                // (4, 3) intentionally left as a hole to break vertical fall.
                5 to 3 to GridTokens.EMPTY, // teleport exit — starts empty.
            ),
        ),
        teleports = listOf(TeleportLinkSpec(srcRow = 3, srcCol = 3, destRow = 5, destCol = 3)),
        disableSpawn = true,
    )

    val RECT_BOARD_SMALL = TestLevel(
        id = 13,
        name = "Rectangular board: 5x3 swap forms a horizontal match",
        // A non-square 5-wide × 3-tall board, proving the engine handles width ≠ height.
        // Swapping the type-2 jelly at (1,2) down with the type-1 jelly at (2,2) lines up
        // three type-1 jellies across row 1, which match detection then clears.
        // Holes everywhere else keep the entity count tiny.
        grid = holesBoard(
            mapOf(
                1 to 1 to pieceIdForJellyType(1),
                1 to 2 to pieceIdForJellyType(2),
                1 to 3 to pieceIdForJellyType(1),
                2 to 2 to pieceIdForJellyType(1),
            ),
            width = 5,
            height = 3,
        ),
        disableSpawn = true,
    )

    val INGREDIENT_MATCH_BASIC = TestLevel(
        id = 14,
        name = "E1: Magical ingredients — swap forms an Ember Seed match",
        // E1 themed-reskin proof: the board is seeded with named magical ingredients
        // (Ember Seed / Moon Crystal) rather than generic jellies. Swapping the
        // Moon Crystal at (3,2) down with the Ember Seed at (4,2) lines up three
        // Ember Seeds across row 3, which match detection then clears. Holes elsewhere
        // and disabled spawn keep the scenario deterministic.
        grid = holesBoard(
            mapOf(
                3 to 1 to PieceIds.EMBER_SEED,
                3 to 2 to PieceIds.MOON_CRYSTAL,
                3 to 3 to PieceIds.EMBER_SEED,
                4 to 2 to PieceIds.EMBER_SEED,
            ),
        ),
        disableSpawn = true,
    )

    val AMBER_BREAK_BY_MATCH = TestLevel(
        id = 15,
        name = "Amber: break by adjacent match",
        // E2: a Crystallized Amber blocker (life 1) at (2,2). Swapping the Moon Crystal
        // at (3,2) down with the Ember Seed at (4,2) lines up three Ember Seeds across
        // row 3; cell (3,2) sits directly below the amber, so the match cracks it and
        // frees a draggable ingredient. Holes + disabled spawn keep it deterministic.
        grid = holesBoard(
            mapOf(
                2 to 2 to GridTokens.ICE_CUBE_LIFE_1,
                3 to 1 to PieceIds.EMBER_SEED,
                3 to 2 to PieceIds.MOON_CRYSTAL,
                3 to 3 to PieceIds.EMBER_SEED,
                4 to 2 to PieceIds.EMBER_SEED,
            ),
        ),
        disableSpawn = true,
    )

    val AMBER_BREAK_BY_BOMB = TestLevel(
        id = 16,
        name = "Amber: break by bomb blast",
        // E2: a bomb at (3,3) with a Crystallized Amber blocker (life 1) at (3,4), inside
        // the bomb's 3×3 blast. Double-clicking the bomb detonates it, the blast cracks
        // the amber, and the trapped ingredient is freed.
        grid = holesBoard(
            mapOf(
                3 to 3 to PieceIds.BOMB,
                3 to 4 to GridTokens.ICE_CUBE_LIFE_1,
            ),
        ),
        disableSpawn = true,
    )

    val POTION_BREWING_WIN = TestLevel(
        id = 17,
        name = "Potion Brewing: collect 3 Moon Crystals to brew",
        // E3: a Potion Brewing objective. The goal is to collect 3 Moon Crystals within
        // 15 moves. Swapping the Ember Seed at (3,2) down with the Moon Crystal at (4,2)
        // lines up three Moon Crystals across row 3, collecting exactly 3 → goal met → win.
        grid = holesBoard(
            mapOf(
                3 to 1 to PieceIds.MOON_CRYSTAL,
                3 to 2 to PieceIds.EMBER_SEED,
                3 to 3 to PieceIds.MOON_CRYSTAL,
                4 to 2 to PieceIds.MOON_CRYSTAL,
            ),
        ),
        moveLimit = 15,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val POTION_BREWING_LOSS = TestLevel(
        id = 18,
        name = "Potion Brewing: out of moves before the brew is ready",
        // E3 loss path: the goal needs 3 Moon Crystals but only one move is allowed, and the
        // single available swap clears Ember Seeds (the wrong ingredient). After the move the
        // counter hits 0 with the goal unmet → loss.
        grid = holesBoard(
            mapOf(
                3 to 1 to PieceIds.EMBER_SEED,
                3 to 2 to PieceIds.MOON_CRYSTAL,
                3 to 3 to PieceIds.EMBER_SEED,
                4 to 2 to PieceIds.EMBER_SEED,
            ),
        ),
        moveLimit = 1,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val CONJURER_REPLACE = TestLevel(
        id = 19,
        name = "Conjurer: collect Moon Crystals to transmute 3 jellies",
        // E4: the Conjurer familiar triggers on Moon Crystals. Matching Moon Crystals
        // fills its gauge; at 12 collected it transmutes 3 random standard jellies on the
        // board into Moon Crystals. This is a full playable board with spawn points (no
        // disableSpawn) so the board keeps refilling, and the goal target is high so the
        // level is NOT won when the gauge reaches its threshold — letting the activation be
        // observed in normal play. Swapping the Ember Seed at (0,2) down with the Moon
        // Crystal at (1,2) lines up three Moon Crystals across row 0 to collect.
        grid = baseBoard(
            mapOf(
                0 to 0 to PieceIds.MOON_CRYSTAL,
                0 to 1 to PieceIds.MOON_CRYSTAL,
                0 to 2 to PieceIds.EMBER_SEED,
                1 to 2 to PieceIds.MOON_CRYSTAL,
            ),
        ),
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 30)),
        familiar = FamiliarType.CONJURER,
    )

    val CURSED_NO_GOAL_CREDIT = TestLevel(
        id = 20,
        name = "Cursed: no goal credit",
        // E5: cursed Moon Crystals (shadow veil) match and score but do NOT advance the
        // collection goal. Swapping the Ember Seed at (3,2) down with the cursed Moon
        // Crystal at (4,2) lines up three cursed Moon Crystals across row 3; they clear
        // and award score, yet the "collect 3 Moon Crystals" goal stays at 3.
        grid = holesBoard(
            mapOf(
                3 to 1 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                3 to 2 to PieceIds.EMBER_SEED,
                3 to 3 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                4 to 2 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
            ),
        ),
        moveLimit = 3,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val CURSED_RUNE_CLEANSE = TestLevel(
        id = 21,
        name = "Cursed: rune cleanse",
        // E5: Purified Runes (glowing rings) under row 3. The same swap as in
        // CURSED_NO_GOAL_CREDIT lines up three cursed Moon Crystals — but here every
        // matched cell sits on a rune, so all three are cleansed as they match and the
        // "collect 3 Moon Crystals" goal completes → win.
        grid = holesBoard(
            mapOf(
                3 to 1 to "${PieceIds.PURIFIED_RUNE}:${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                3 to 2 to "${PieceIds.PURIFIED_RUNE}:${PieceIds.EMBER_SEED}",
                3 to 3 to "${PieceIds.PURIFIED_RUNE}:${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                4 to 2 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
            ),
        ),
        moveLimit = 5,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val VOID_PUDDLE_CURSE = TestLevel(
        id = 22,
        name = "Void Puddle: curse on landing only",
        // E5: only ingredients that come to REST on a puddle are cursed — falling
        // through one leaves no mark. Two hole-bounded channels, released by
        // double-clicking the bomb at (6,0):
        //  - column 2: the Moon Crystal falls (0,2)→(2,2) and rests ON the puddle
        //    (the cell below is a hole) → cursed.
        //  - column 4: the Moon Crystal falls THROUGH the puddle at (1,4) and rests
        //    on the plain cell at (3,4) → stays clean.
        grid = holesBoard(
            mapOf(
                6 to 0 to PieceIds.BOMB,
                0 to 2 to PieceIds.MOON_CRYSTAL,
                1 to 2 to GridTokens.EMPTY,
                2 to 2 to PieceIds.VOID_PUDDLE,
                0 to 4 to PieceIds.MOON_CRYSTAL,
                1 to 4 to PieceIds.VOID_PUDDLE,
                2 to 4 to GridTokens.EMPTY,
                3 to 4 to GridTokens.EMPTY,
            ),
        ),
        moveLimit = 5,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val VOID_PUDDLE_LANDING_MATCH = TestLevel(
        id = 23,
        name = "Void Puddle: landing curse spoils the match",
        // E5: the landing curse is stamped at the stable-board point BEFORE match
        // detection counts goals, and one cursed ingredient spoils the whole line.
        // Double-clicking the bomb at (6,0) (isolated — its blast hits only itself)
        // releases the Moon Crystal at (1,2); it falls onto the puddle at (3,2) and
        // completes the row-3 match as it lands. Because it is cursed on landing, the
        // entire row-3 match is spoiled: it clears but the "collect 3" goal stays at 3
        // and the match scores nothing (the only points are the bomb's own 10).
        grid = holesBoard(
            mapOf(
                6 to 0 to PieceIds.BOMB,
                1 to 2 to PieceIds.MOON_CRYSTAL,
                2 to 2 to GridTokens.EMPTY,
                3 to 1 to PieceIds.MOON_CRYSTAL,
                3 to 2 to PieceIds.VOID_PUDDLE,
                3 to 3 to PieceIds.MOON_CRYSTAL,
            ),
        ),
        moveLimit = 5,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val CURSED_AREA_EFFECT = TestLevel(
        id = 24,
        name = "Cursed: area effect counts only clean items",
        // E5: unlike a match (all-or-nothing), an area effect is scored per-item — clean
        // items in the blast still count. The bomb at (3,3) blasts its 3×3; the only
        // ingredients inside are one cursed and two clean Moon Crystals. Detonating
        // collects the two clean crystals (goal 5 → 3) and scores them plus the bomb;
        // the cursed crystal clears but gives nothing.
        grid = holesBoard(
            mapOf(
                3 to 3 to PieceIds.BOMB,
                2 to 2 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                2 to 3 to PieceIds.MOON_CRYSTAL,
                2 to 4 to PieceIds.MOON_CRYSTAL,
            ),
        ),
        moveLimit = 5,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 5)),
        disableSpawn = true,
    )

    val CURSED_SPOILS_ONLY_ITS_OWN_MATCH = TestLevel(
        id = 25,
        name = "Cursed: spoils only its own match line",
        // E5: spoiling is per-group. One swap forms two separate vertical 3-matches at
        // once — an Ember Seed line in column 1 (clean) and a Moon Crystal line in
        // column 2 (which holds a cursed crystal at (1,2)). Swapping (3,1)↔(3,2) stacks
        // each colour: the Ember line clears and scores (30); the Moon line is spoiled
        // (no score, no goal credit). Proves a cursed line does not taint a clean line
        // resolved in the same detection pass.
        grid = holesBoard(
            mapOf(
                1 to 1 to PieceIds.EMBER_SEED,
                1 to 2 to "${GridTokens.CURSED_PREFIX}${PieceIds.MOON_CRYSTAL}",
                2 to 1 to PieceIds.EMBER_SEED,
                2 to 2 to PieceIds.MOON_CRYSTAL,
                3 to 1 to PieceIds.MOON_CRYSTAL,
                3 to 2 to PieceIds.EMBER_SEED,
            ),
        ),
        moveLimit = 5,
        goals = listOf(LevelGoalConfig(pieceId = PieceIds.MOON_CRYSTAL, target = 3)),
        disableSpawn = true,
    )

    val TUTORIAL_GUARANTEED_FIRST_SWAP = TestLevel(
        id = 26,
        name = "Tutorial: guaranteed first swap",
        // E16 (R1 slice, issue #110): deterministic board for GameViewModel.firstAvailableSwapHint().
        // Swapping the Moon Crystal at (3,2) down with the Ember Seed at (4,2) lines up three
        // Ember Seeds across row 3 — the single available match-producing swap the hint must find.
        grid = holesBoard(
            mapOf(
                3 to 1 to PieceIds.EMBER_SEED,
                3 to 2 to PieceIds.MOON_CRYSTAL,
                3 to 3 to PieceIds.EMBER_SEED,
                4 to 2 to PieceIds.EMBER_SEED,
            ),
        ),
        disableSpawn = true,
    )

    val all: List<TestLevel> = listOf(
        BOMB_DOUBLE_CLICK,
        BOMB_SWAP_ACTIVATION,
        LASER_H_SWAP_ACTIVATION,
        LASER_H_DOUBLE_CLICK,
        LASER_V_DOUBLE_CLICK,
        BOMB_TRIGGERS_LASER_IN_BLAST,
        LASER_THROUGH_BOMB,
        SWIRL_DOUBLE_CLICK,
        MOVE_LIMIT_LOST_GAME,
        MOVE_LIMIT_DECREMENT,
        TARGETED_REMOVE,
        TELEPORT_BASIC,
        RECT_BOARD_SMALL,
        INGREDIENT_MATCH_BASIC,
        AMBER_BREAK_BY_MATCH,
        AMBER_BREAK_BY_BOMB,
        POTION_BREWING_WIN,
        POTION_BREWING_LOSS,
        CONJURER_REPLACE,
        CURSED_NO_GOAL_CREDIT,
        CURSED_RUNE_CLEANSE,
        VOID_PUDDLE_CURSE,
        VOID_PUDDLE_LANDING_MATCH,
        CURSED_AREA_EFFECT,
        CURSED_SPOILS_ONLY_ITS_OWN_MATCH,
        TUTORIAL_GUARANTEED_FIRST_SWAP,
    )

    fun byId(id: Int): TestLevel? = all.firstOrNull { it.id == id }
}
